import { useRef } from 'react';
import { usePaymentAdmin } from './hooks/usePaymentAdmin';
import { formatDate, formatCurrency, getStatusColor, downloadFile } from './utils/paymentUtils';
import PaymentStatusBadge from '../../../component/components/admin/PaymentStatusBadge';
import PaymentActions from '../../../component/components/admin/PaymentActions';
import PaymentFilters from '../../../component/components/admin/PaymentFilters';
import { generateFormLink } from '../../../component/components/admin/formlinks';
import { 
  FiRefreshCw, FiDownload, FiFilter, FiSearch, FiUpload, 
  FiCheckCircle, FiAlertCircle, FiChevronLeft, FiChevronRight,
  FiChevronsLeft, FiChevronsRight, FiExternalLink, FiUser,
  FiFileText, FiDollarSign, FiCreditCard, FiCalendar,
  FiTag, FiSliders, FiChevronUp, FiChevronDown
} from 'react-icons/fi';

// Helper functions
const getSortIndicator = (key, sortConfig) => {
  if (sortConfig.key !== key) return null;
  return sortConfig.direction === 'asc' ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />;
};

const getTotalAmount = (payments) => {
  const total = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  return formatCurrency(total);
};

const exportToCSV = (data) => {
  if (!data || data.length === 0) return;
  
  const headers = [
    'ID', 'Name', 'Email', 'Form Type', 'Amount', 'Transaction ID', 
    'Status', 'Created Date', 'Updated Date'
  ];
  
  const csvRows = [headers.join(',')];
  
  data.forEach(item => {
    const row = [
      item._id || '',
      `"${item.name || ''}"`,
      `"${item.email || ''}"`,
      `"${item.formType || ''}"`,
      item.amount || 0,
      `"${item.transactionId || ''}"`,
      `"${item.status || ''}"`,
      item.createdAt ? new Date(item.createdAt).toISOString() : '',
      item.updatedAt ? new Date(item.updatedAt).toISOString() : ''
    ];
    csvRows.push(row.join(','));
  });
  
  const csvContent = csvRows.join('\n');
  const fileName = `payment_export_${new Date().toISOString().split('T')[0]}.csv`;
  
  downloadFile(csvContent, fileName, 'text/csv');
};

const paymentAdmin = () => {
  const fileInputRef = useRef(null);
  const {
    // State
    payments, filteredPayments, loading, statusFilter, searchTerm, 
    formTypeFilter, amountRange, currentPage, totalPages, isFilterExpanded, 
    sortConfig, selectedPayments, selectAll, isVerifying, verificationResults, 
    transactionIdsToVerify, verificationMode, bankStatementFile, verificationStats,
    
    // Actions
    setStatusFilter, setSearchTerm, setFormTypeFilter, setAmountRange,
    setIsFilterExpanded, setTransactionIdsToVerify, setVerificationMode,
    
    // Handlers
    fetchPayments, getPaginatedData, handlePageChange, handleSort,
    handleSelectAll, handleSelectPayment, handleVerifyPayment, handleRejectPayment,
    handleRevertStatus, handleBulkAction, handleVerifyTransactionIds,
    handleVerifyAllMatched, handleFileChange
  } = usePaymentAdmin();

  return (
    <div className="min-h-screen bg-gray-50 p-4 mt-16 md:p-8">
      {/* Main Dashboard Container */}
      <div className="max-w-8xl mx-auto rounded-lg bg-white overflow-hidden border border-gray-200">
        {/* Dashboard Header */}
        <div className="bg-white px-8 py-6 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Payment Management</h1>
            <p className="text-gray-500 mt-1">Monitor and verify financial transactions</p>
          </div>
          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            <div className="bg-green-50 text-green-800 rounded-lg px-4 py-2 text-sm font-medium border border-green-100">
              <span>Records:</span> 
              <span className="ml-1 font-medium">{filteredPayments.length}</span>
            </div>
            <div className="bg-green-50 text-green-800 rounded-lg px-4 py-2 text-sm font-medium border border-green-100">
              <span>Total:</span> 
              <span className="ml-1 font-medium">{getTotalAmount(filteredPayments)}</span>
            </div>
          </div>
        </div>

        {/* Control Bar */}
        <div className="bg-white px-8 py-4 border-b flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <button 
              onClick={fetchPayments} 
              className="btn-action bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
            >
              <FiRefreshCw className="mr-2" /> Refresh
            </button>
            <button 
              onClick={() => exportToCSV(filteredPayments)} 
              className="btn-action bg-green-600 text-white hover:bg-green-700 border border-green-700"
            >
              <FiDownload className="mr-2" /> Export
            </button>
            <button 
              onClick={() => setIsFilterExpanded(!isFilterExpanded)} 
              className={`btn-action ${isFilterExpanded ? 'bg-gray-100 text-gray-800' : 'bg-white text-gray-700'} hover:bg-gray-50 border border-gray-200`}
            >
              <FiSliders className="mr-2" /> Filters
            </button>
          </div>
          
          <div className="relative flex-grow max-w-lg">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search payments..."
              className="block w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all text-sm"
            />
          </div>
        </div>

        {/* Verification Panel */}
        {verificationMode !== 'none' && (
          <div className="px-8 py-5 border-b bg-gray-50">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-md font-medium text-gray-800">
                  Transaction Verification
                </h3>
                <div className="flex space-x-2">
                  <button 
                    className={`px-3 py-1 text-sm rounded-md ${verificationMode === 'manual' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    onClick={() => setVerificationMode('manual')}
                  >
                    Manual
                  </button>
                  <button 
                    className={`px-3 py-1 text-sm rounded-md ${verificationMode === 'file' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                    onClick={() => setVerificationMode('file')}
                  >
                    Bank Statement
                  </button>
                </div>
              </div>
              
              <div className="p-4">
                {verificationMode === 'manual' && (
                  <div className="mb-4">
                    <label htmlFor="transactionIds" className="block text-sm font-medium text-gray-700 mb-2">
                      Transaction IDs to Verify
                    </label>
                    <textarea
                      id="transactionIds"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                      rows="3"
                      placeholder="Paste transaction IDs..."
                      value={transactionIdsToVerify}
                      onChange={(e) => setTransactionIdsToVerify(e.target.value)}
                    ></textarea>
                  </div>
                )}
                
                {verificationMode === 'file' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Bank Statement
                    </label>
                    <div className="flex items-center space-x-3">
                      <div 
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-1 cursor-pointer"
                      >
                        <div className={`border border-dashed rounded-md p-3 text-center ${bankStatementFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-green-400'}`}>
                          {bankStatementFile ? (
                            <>
                              <FiFileText className="mx-auto h-6 w-6 text-green-500" />
                              <p className="mt-1 text-sm text-gray-700 truncate">{bankStatementFile.name}</p>
                            </>
                          ) : (
                            <>
                              <FiUpload className="mx-auto h-6 w-6 text-gray-400" />
                              <p className="mt-1 text-sm text-gray-700">Click to browse</p>
                            </>
                          )}
                        </div>
                      </div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept=".csv,.txt,.pdf"
                        onChange={handleFileChange}
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex items-center justify-between mt-4">
                  <div className="text-xs text-gray-500">
                    {verificationMode === 'manual' 
                      ? 'Enter transaction IDs to verify against payments'
                      : 'Upload bank statement to verify transactions'}
                  </div>
                  <button
                    onClick={handleVerifyTransactionIds}
                    disabled={isVerifying || (verificationMode === 'manual' && !transactionIdsToVerify.trim()) || (verificationMode === 'file' && !bankStatementFile)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 flex items-center text-sm"
                  >
                    {isVerifying ? (
                      <>
                        <div className="h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <FiCheckCircle className="mr-2" />
                        Verify
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Verification Results */}
              {verificationResults && (
                <div className="border-t">
                  <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">Verification Results</h4>
                    <div className="flex items-center space-x-3">
                      <div className="text-xs">
                        <span className="text-gray-500">Total:</span> 
                        <span className="ml-1 font-medium">{verificationStats.total}</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-green-600">Matched:</span> 
                        <span className="ml-1 font-medium">{verificationStats.matched}</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-blue-600">Verified:</span> 
                        <span className="ml-1 font-medium">{verificationStats.alreadyVerified}</span>
                      </div>
                      <div className="text-xs">
                        <span className="text-red-600">Unmatched:</span> 
                        <span className="ml-1 font-medium">{verificationStats.unmatched}</span>
                      </div>
                      {verificationResults.matched.length > 0 && (
                        <button
                          onClick={handleVerifyAllMatched}
                          className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center text-xs"
                        >
                          <FiCheckCircle className="mr-1" />
                          Verify All
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 divide-x divide-y lg:divide-y-0 border-t">
                    {/* Matched Transactions */}
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-medium text-green-700">
                          Matched
                        </h5>
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                          {verificationResults.matched.length}
                        </span>
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                        {verificationResults.matched.length > 0 ? (
                          verificationResults.matched.map((item, index) => (
                            <div key={index} className="p-2 bg-green-50 rounded-md border border-green-100 text-xs">
                              <div className="font-medium">{item.transactionId}</div>
                              <div className="text-green-700">{item.payment.name}</div>
                              <div className="text-gray-500">{formatDate(item.payment.createdAt)}</div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-3 text-gray-400 text-sm">
                            No matches found
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Already Verified */}
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-medium text-blue-700">
                          Verified
                        </h5>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                          {verificationResults.alreadyVerified.length}
                        </span>
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                        {verificationResults.alreadyVerified.length > 0 ? (
                          verificationResults.alreadyVerified.map((item, index) => (
                            <div key={index} className="p-2 bg-blue-50 rounded-md border border-blue-100 text-xs">
                              <div className="font-medium">{item.transactionId}</div>
                              <div className="text-blue-700">{item.payment.name}</div>
                              <div className="text-gray-500">{formatDate(item.payment.createdAt)}</div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-3 text-gray-400 text-sm">
                            No previously verified
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Unmatched Transactions */}
                    <div className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-medium text-red-700">
                          Unmatched
                        </h5>
                        <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full">
                          {verificationResults.unmatched.length}
                        </span>
                      </div>
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                        {verificationResults.unmatched.length > 0 ? (
                          verificationResults.unmatched.map((item, index) => (
                            <div key={index} className="p-2 bg-red-50 rounded-md border border-red-100 text-xs">
                              <div className="font-medium">{item.transactionId}</div>
                              <div className="text-red-600">No matching payment</div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-3 text-gray-400 text-sm">
                            All matched
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filters Section */}
        {isFilterExpanded && (
          <div className="px-8 py-4 border-b bg-gray-50">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="text-md font-medium text-gray-800 mb-3">
                Advanced Filters
              </h4>
              <PaymentFilters
                filter={statusFilter}
                onFilterChange={(e) => setStatusFilter(e.target.value)}
                onRefresh={fetchPayments}
                searchTerm={searchTerm}
                onSearchChange={(e) => setSearchTerm(e.target.value)}
                formTypeFilter={formTypeFilter}
                onFormTypeFilterChange={(e) => setFormTypeFilter(e.target.value)}
                amountRange={amountRange}
                onAmountRangeChange={(e) => setAmountRange(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {selectedPayments.length > 0 && (
          <div className="px-8 py-2 bg-green-50 border-b flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-green-800 text-sm flex items-center">
                <FiCheckCircle className="mr-2" />
                {selectedPayments.length} selected
              </span>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={() => handleBulkAction('verify')} 
                className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 flex items-center"
              >
                <FiCheckCircle className="mr-1" />
                Verify
              </button>
              <button 
                onClick={() => handleBulkAction('reject')} 
                className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 flex items-center"
              >
                <FiAlertCircle className="mr-1" />
                Reject
              </button>
            </div>
          </div>
        )}

        {/* Payments Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 flex flex-col items-center justify-center">
              <div className="h-8 w-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-3 text-gray-600">Loading payments...</p>
            </div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                      />
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Name {getSortIndicator('name', sortConfig)}
                      </div>
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('formType')}
                    >
                      <div className="flex items-center">
                        Form Type {getSortIndicator('formType', sortConfig)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Link
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('amount')}
                    >
                      <div className="flex items-center">
                        Amount {getSortIndicator('amount', sortConfig)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coupon
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transaction ID
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      Status {getSortIndicator('status', sortConfig)}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center">
                        Date {getSortIndicator('createdAt', sortConfig)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getPaginatedData().length > 0 ? getPaginatedData().map(payment => (
                    <tr key={payment._id} className={`hover:bg-gray-50 ${selectedPayments.includes(payment._id) ? 'bg-green-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedPayments.includes(payment._id)}
                          onChange={() => handleSelectPayment(payment._id)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-gray-900">{payment.name || '-'}</div>
                        {payment.email && <div className="text-xs text-gray-500 mt-1">{payment.email}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                          {payment.formType || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.formType && payment.uniqueFormId ? (
                          <a
                            href={generateFormLink(payment.formType, payment.uniqueFormId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-800 hover:underline text-sm"
                          >
                            View
                          </a>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {payment.couponCode ? (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                            {payment.couponCode}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-xs text-gray-700">
                        {payment.transactionId || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <PaymentStatusBadge status={payment.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                        {formatDate(payment.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <PaymentActions
                          payment={payment}
                          onVerify={handleVerifyPayment}
                          onReject={handleRejectPayment}
                          onRevert={handleRevertStatus}
                        />
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="10" className="px-6 py-8 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="h-12 w-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <h4 className="text-md font-medium text-gray-700">No payments found</h4>
                          <p className="text-sm text-gray-500 mt-1">
                            Adjust your search or filter criteria
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-8 py-4 border-t bg-white flex flex-col sm:flex-row justify-between items-center">
                  <div className="mb-3 sm:mb-0 text-xs text-gray-600">
                    Showing <span className="font-medium">{(currentPage - 1) * 25 + 1}</span> to <span className="font-medium">
                      {Math.min(currentPage * 25, filteredPayments.length)}
                    </span> of <span className="font-medium">{filteredPayments.length}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button 
                      onClick={() => handlePageChange(1)} 
                      disabled={currentPage === 1} 
                      className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      <FiChevronsLeft className="h-3 w-3 text-gray-600" />
                    </button>
                    <button 
                      onClick={() => handlePageChange(currentPage - 1)} 
                      disabled={currentPage === 1} 
                      className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      <FiChevronLeft className="h-3 w-3 text-gray-600" />
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = totalPages <= 5 ? i + 1 :
                        currentPage <= 3 ? i + 1 :
                        currentPage >= totalPages - 2 ? totalPages - 4 + i :
                        currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-md border flex items-center justify-center text-xs ${pageNum === currentPage ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button 
                      onClick={() => handlePageChange(currentPage + 1)} 
                      disabled={currentPage === totalPages} 
                      className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      <FiChevronRight className="h-3 w-3 text-gray-600" />
                    </button>
                    <button 
                      onClick={() => handlePageChange(totalPages)} 
                      disabled={currentPage === totalPages} 
                      className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      <FiChevronsRight className="h-3 w-3 text-gray-600" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default paymentAdmin;